import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { PaymentHistoryService } from '../services/PaymentAccountService';
import type { PaymentHistorySummary } from '../services/PaymentAccountService';
import type { PaymentHistory, PaginationInfo } from '../types';

const SEARCH_DEBOUNCE_MS = 400;

export interface PaymentHistoryFilters {
  search?: string;
  status?: string[];
  chargeType?: string[];
  dateStart?: string;
  dateEnd?: string;
}

export function usePaymentHistory(
  paymentAccountId: number,
  subscriptionId: number | null | undefined,
  page: number,
  pageSize: number,
  filters: PaymentHistoryFilters = {},
) {
  const [history, setHistory] = useState<PaymentHistory[]>([]);
  const [summary, setSummary] = useState<PaymentHistorySummary>({
    totalPaid: 0,
    paidCount: 0,
    lastPaymentDate: null,
    totalsByCurrency: {
      BRL: 0,
      USD: 0,
    },
  });
  const [pagination, setPagination] = useState<PaginationInfo>({
    page,
    pageSize,
    totalRecords: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });
  const [loading, setLoading] = useState(true);

  const reqId = useRef(0);

  const fetchPage = useCallback(
    async (page: number, ps: number = pageSize, f: PaymentHistoryFilters = {}) => {
      const currentReq = ++reqId.current;
      try {
        setLoading(true);

        const response = await PaymentHistoryService.listPaginated(page, ps, paymentAccountId, {
          subscriptionId: subscriptionId ?? undefined,
          search: f.search,
          status: f.status,
          chargeType: f.chargeType,
          dateStart: f.dateStart,
          dateEnd: f.dateEnd,
        });

        if (currentReq !== reqId.current) return;

        setHistory(response.data);
        setPagination(response.pagination);
        setSummary(response.summary);
      } catch {
        if (currentReq !== reqId.current) return;
        setHistory([]);
      } finally {
        if (currentReq === reqId.current) setLoading(false);
      }
    },
    [paymentAccountId, subscriptionId, pageSize],
  );

  // Debounce search, react immediately to other filter changes
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prevFilters = useRef(filters);

  const filtersKey = useMemo(
    () => JSON.stringify([filters.search, filters.status, filters.chargeType, filters.dateStart, filters.dateEnd]),
    [filters.search, filters.status, filters.chargeType, filters.dateStart, filters.dateEnd],
  );

  useEffect(() => {
    const onlySearchChanged =
      JSON.stringify(filters.status) === JSON.stringify(prevFilters.current.status) &&
      JSON.stringify(filters.chargeType) === JSON.stringify(prevFilters.current.chargeType) &&
      filters.dateStart === prevFilters.current.dateStart &&
      filters.dateEnd === prevFilters.current.dateEnd &&
      filters.search !== prevFilters.current.search;

    prevFilters.current = filters;

    if (onlySearchChanged) {
      clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        fetchPage(page, pageSize, filters);
      }, SEARCH_DEBOUNCE_MS);
    } else {
      clearTimeout(debounceTimer.current);
      fetchPage(page, pageSize, filters);
    }

    return () => clearTimeout(debounceTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, fetchPage, page, pageSize]);

  const refresh = useCallback(
    () => fetchPage(page, pageSize, filters),
    [fetchPage, page, pageSize, filters],
  );

  const optimisticUpdate = useCallback(
    (id: number, patch: Partial<PaymentHistory>) => {
      setHistory((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    },
    [],
  );

  const silentRefresh = useCallback(async () => {
    const currentReq = ++reqId.current;
    try {
      const response = await PaymentHistoryService.listPaginated(
        page,
        pageSize,
        paymentAccountId,
        {
          subscriptionId: subscriptionId ?? undefined,
          search: filters.search,
          status: filters.status,
          chargeType: filters.chargeType,
          dateStart: filters.dateStart,
          dateEnd: filters.dateEnd,
        },
      );
      if (currentReq !== reqId.current) return;
      setHistory(response.data);
      setPagination(response.pagination);
      setSummary(response.summary);
    } catch {
      // silent — don't clear data
    }
  }, [page, pageSize, paymentAccountId, subscriptionId, filters]);

  return {
    history,
    summary,
    pagination,
    loading,
    refresh,
    silentRefresh,
    optimisticUpdate,
  };
}
